import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Lock, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Container } from "@/components/Section";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { value: "all", label: "All" },
  { value: "short_term", label: "Short-Term" },
  { value: "corporate", label: "Corporate 21+" },
  { value: "unsure", label: "Unsure" },
];

const STATUSES = ["new", "contacted", "assessment", "onboarded"];

const STATUS_STYLES = {
  new: "bg-gold/15 text-gold border-gold/30",
  contacted: "bg-sage/15 text-sage-light border-sage/30",
  assessment: "bg-forest-700/40 text-cream border-sage/30",
  onboarded: "bg-forest-700 text-cream border-forest-700",
};

const ENQUIRY_LABELS = { short_term: "Short-term", corporate: "Corporate 21+", unsure: "Unsure" };

export default function Admin() {
  const [key, setKey] = useState(() => sessionStorage.getItem("fg_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (k) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`, { headers: { "x-admin-key": k } });
      setLeads(res.data);
      setAuthed(true);
      sessionStorage.setItem("fg_admin_key", k);
    } catch {
      setAuthed(false);
      toast.error("Incorrect access key.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}/status`, { status }, { headers: { "x-admin-key": key } });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Status updated.");
    } catch {
      toast.error("Could not update status.");
    }
  };

  const resyncProperties = async () => {
    try {
      const res = await axios.post(`${API}/properties/sync`, {}, { headers: { "x-admin-key": key } });
      toast.success(`${res.data.synced} properties synced from your booking site.`);
    } catch {
      toast.error("Property sync failed.");
    }
  };

  const removeLead = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`, { headers: { "x-admin-key": key } });
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    } catch {
      toast.error("Could not delete lead.");
    }
  };

  const filtered = leads.filter((l) => filter === "all" || l.enquiry_type === filter);

  if (!authed) {
    return (
      <main className="noise-dark flex min-h-screen items-center justify-center bg-forest px-6 text-cream" data-testid="admin-login">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load(key);
          }}
          className="w-full max-w-sm rounded-2xl border border-white/10 bg-forest-800/80 p-9 backdrop-blur-xl"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sage/15 text-sage">
            <Lock className="h-5 w-5" />
          </span>
          <h1 className="mt-6 font-serif text-3xl font-medium">Leads Dashboard</h1>
          <p className="mt-2 text-sm text-cream/60">Enter your access key to view property assessment enquiries.</p>
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Access key"
            data-testid="admin-key-input"
            className="mt-6 w-full rounded-lg border border-white/15 bg-forest px-4 py-3 text-sm text-cream placeholder:text-cream/30 focus:border-sage"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cream px-6 py-3.5 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-sage disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            View Leads
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="noise-dark min-h-screen bg-forest pb-24 pt-32 text-cream" data-testid="admin-dashboard">
      <Container className="relative z-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow text-sage">FieldGreen · Internal</p>
            <h1 className="mt-3 font-serif text-4xl font-medium sm:text-5xl">Property Assessment Leads</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={resyncProperties}
              data-testid="admin-resync-properties-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-cream/80 transition-colors duration-300 hover:border-sage hover:text-sage-light"
            >
              <RefreshCw className="h-4 w-4" /> Resync Properties
            </button>
            <button
              onClick={() => load(key)}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-cream/80 transition-colors duration-300 hover:border-sage hover:text-sage-light"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
          </div>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {[
            { label: "Total leads", value: leads.length, id: "total" },
            { label: "New", value: leads.filter((l) => l.status === "new").length, id: "new" },
            { label: "Corporate 21+", value: leads.filter((l) => l.enquiry_type === "corporate").length, id: "corporate" },
          ].map((s) => (
            <div key={s.id} className="rounded-xl border border-white/10 bg-forest-800/70 p-6" data-testid={`admin-stat-${s.id}`}>
              <p className="font-mono text-3xl font-semibold text-cream">{s.value}</p>
              <p className="mt-1 text-xs uppercase tracking-[0.18em] text-cream/50">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-filter-tabs">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              data-testid={`admin-filter-${f.value}`}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-300 ${
                filter === f.value ? "bg-cream text-forest" : "border border-white/15 text-cream/60 hover:text-cream"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10" data-testid="admin-leads-table">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-forest-800/80 text-xs uppercase tracking-[0.15em] text-cream/50">
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Contact</th>
                <th className="px-5 py-4">Property</th>
                <th className="px-5 py-4">Enquiry</th>
                <th className="px-5 py-4">Submitted</th>
                <th className="px-5 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-cream/40" data-testid="admin-empty-state">
                    No leads yet. New property assessment enquiries will appear here.
                  </td>
                </tr>
              )}
              {filtered.map((l) => (
                <tr key={l.id} className="border-b border-white/5 align-top transition-colors duration-300 hover:bg-white/[0.03]" data-testid={`admin-lead-row-${l.id}`}>
                  <td className="px-5 py-4">
                    <p className="font-semibold text-cream">{l.name}</p>
                    {l.message && <p className="mt-1 max-w-[220px] text-xs text-cream/45 line-clamp-2">{l.message}</p>}
                  </td>
                  <td className="px-5 py-4 text-cream/70">
                    <p>{l.email}</p>
                    <p className="text-cream/45">{l.phone}</p>
                  </td>
                  <td className="px-5 py-4 text-cream/70">
                    <p>{l.location}</p>
                    <p className="text-xs text-cream/45">
                      {[l.property_type, l.bedrooms && `${l.bedrooms} bed`, l.rental_situation].filter(Boolean).join(" · ")}
                    </p>
                  </td>
                  <td className="px-5 py-4">
                    <span className="rounded-full border border-sage/25 bg-sage/10 px-3 py-1 text-xs font-medium text-sage-light">
                      {ENQUIRY_LABELS[l.enquiry_type] || l.enquiry_type}
                    </span>
                  </td>
                  <td className="px-5 py-4 font-mono text-xs text-cream/50">
                    {new Date(l.created_at).toLocaleDateString("en-IE", { day: "numeric", month: "short" })}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <select
                        value={l.status}
                        onChange={(e) => setStatus(l.id, e.target.value)}
                        data-testid={`admin-status-select-${l.id}`}
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold capitalize ${STATUS_STYLES[l.status] || STATUS_STYLES.new} bg-forest cursor-pointer`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => removeLead(l.id)}
                        data-testid={`admin-delete-lead-${l.id}`}
                        aria-label="Delete lead"
                        className="flex h-7 w-7 items-center justify-center rounded-full border border-white/10 text-cream/40 transition-colors duration-300 hover:border-red-400/40 hover:text-red-300"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Container>
    </main>
  );
}
